import { Command } from './Command';
import { updateOverlapStates } from '../components/Editor/utils/overlapDetection';
import { GARDEN_OBJECT_TYPES } from '../constants/gardenObjectTypes';
import { isLinkableParent, syncLinkedGates } from '../components/Editor/tools/gateLinkUtils';

/**
 * ModifyObjectCommand - Modifies object properties
 * Used for: Dimension changes, color changes, position changes, etc.
 *
 * @example
 * const oldProps = { width: 100, height: 50 };
 * const newProps = { width: 150, height: 75 };
 * const command = new ModifyObjectCommand(rect, oldProps, newProps);
 * commandStack.execute(command);
 */
export class ModifyObjectCommand extends Command {
  /**
   * @param {Object} object - Fabric.js object to modify
   * @param {Object} oldProps - Previous property values
   * @param {Object} newProps - New property values
   */
  constructor(object, oldProps, newProps) {
    super();
    this.object = object;
    this.oldProps = { ...oldProps };
    this.newProps = { ...newProps };
  }

  execute() {
    this.object.set(this.newProps);
    this.object.setCoords(); // Update bounding box

    // Geometry changes (vertex edits, dimension inputs) move edges —
    // resync gates linked to this structure (#611)
    if (this.object.canvas && isLinkableParent(this.object)) {
      syncLinkedGates(this.object.canvas, this.object);
    }

    if (this.object.canvas) {
      this.object.canvas.renderAll();
    }
    // Fire modified event so properties panel updates
    this.object.fire('modified');

    // Update overlap detection if this is a plant and its footprint moved:
    // position (left/top) or the width override that resizes the ring (#754).
    if (this.object.objectType === GARDEN_OBJECT_TYPES.PLANT && this.object.canvas) {
      if ('left' in this.newProps || 'top' in this.newProps || 'customWidthCm' in this.newProps) {
        updateOverlapStates(this.object.canvas);
      }
    }
  }

  undo() {
    this.object.set(this.oldProps);
    this.object.setCoords();

    // Resync gates linked to this structure (#611)
    if (this.object.canvas && isLinkableParent(this.object)) {
      syncLinkedGates(this.object.canvas, this.object);
    }

    if (this.object.canvas) {
      this.object.canvas.renderAll();
    }
    // Fire modified event so properties panel updates
    this.object.fire('modified');

    // Update overlap detection if this is a plant and its footprint moved:
    // position (left/top) or the width override that resizes the ring (#754).
    if (this.object.objectType === GARDEN_OBJECT_TYPES.PLANT && this.object.canvas) {
      if ('left' in this.oldProps || 'top' in this.oldProps || 'customWidthCm' in this.oldProps) {
        updateOverlapStates(this.object.canvas);
      }
    }
  }

  getDescription() {
    const type = this.object.data?.type || this.object.type;
    const changedProps = Object.keys(this.newProps).join(', ');
    return `Modify ${type} (${changedProps})`;
  }
}
